import * as d3 from "d3"
export type GradientStopData = {
    style: string,
    offset: string
}
export type AreaGradientData = {
    x1?: string,
    x2?: string,
    y1?: string,
    y2?: string,
    dataClass: string
    stopDatas: GradientStopData[]
}
export type MultipleLineChartOption<T> = {
    y: (x: T) => number, //Data Group 별로 y의 값.
    yDomainCorrection?: (maxY: number) => number
    classification: keyof T, //분류 할 수 있는 컬럼,
    sorting?: (a: any, b: any) => number,
    xRange?: [number, number] //X축의 실제 크기
    yRange?: [number, number] //Y축의 실제 높이
    //그래프의 각 꼭지점 좌표에서 상대적으로 이동하려는 값. 
    areaGradient?: AreaGradientData[],
    margin?: {
        left?: number,
        right?: number,
        top?: number,
        bottom?: number
    },
    mousemoveEvent?: (event: MouseEvent, datas: T[]) => void,
    mouseoutEvent?: (event: MouseEvent) => void
    maximumCircle?: number,
    xAxislabel?: (type: "start" | "midium" | "end") => string
}

export default function MultipleYaxisLineChart<T>(node: HTMLElement, data: T[], option: MultipleLineChartOption<T>) {
    let { width, height } = node.getBoundingClientRect();
    const { margin } = option;
    const {
        top = 0,
        right = 0,
        left = 0,
        bottom = 40 } = margin || {};
    const {
        y,
        yDomainCorrection = (yMax: number) => yMax,
        classification,
        sorting = (a, b) => 0,
        areaGradient = [],
        mousemoveEvent = (e, d) => { },
        mouseoutEvent = () => { },
        maximumCircle = 12,
        xAxislabel = () => ""
    } = option
    let {
        xRange = [right, width - left],
        yRange = [height - bottom, top],
    } = option
    // 
    const classNames = new Set(data.map(v => v[classification]))
    const sameClassesData = [...classNames.values()]
        .map(className => {
            const sameDatas = data.filter((d) => d[classification] === className);
            return {
                datas: sameDatas,
                className: className
            }
        })
        .sort((a, b) => sorting(a.className, b.className));
    //
    const maximumLengthOfDatas = sameClassesData.reduce((prev, cur) => {
        const currentLength: number = cur.datas.length;
        if (currentLength > prev) {
            return currentLength
        } else {
            return prev
        }
    }, 0)

    const Ys = sameClassesData.map(v => d3.map(v.datas, y))
    const yDomains = Ys.map((v) => [0, yDomainCorrection(d3.max(v) as number)])
    const xDomain = [0, maximumLengthOfDatas - 1]//X축의 범위.
    let xScale = d3.scaleLinear(xDomain, xRange);
    let yScales = yDomains.map((v) => {
        return d3.scaleLinear(v as number[], yRange);
    })
    //SVG
    const svg = d3.create("svg")
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", [0, 0, width, height])
        .attr("style", "max-width: 100%;");

    //Defs Gradient
    const defs = svg.append("defs");
    for (const gradient of areaGradient) {
        const { x1 = "0%", x2 = "100%", y1 = "0%", y2 = "100%", dataClass = "", stopDatas = [] } = gradient
        const linear = defs
            .append("linearGradient")
            .attr("id", dataClass)
            .attr("x1", x1)
            .attr("y1", y1)
            .attr("x2", x2)
            .attr("y2", y2)

        for (const stop of stopDatas) {
            const { offset, style } = stop
            linear.append("stop")
                .attr("offset", offset)
                .attr("style", style)
        }
    }

    //Circle generater 
    const lineGroups = svg.append("g")
    const dashboardGroups = svg.append("g");
    let circles: SVGElement[] = []
    let circleGroups = svg.append("g").attr("id", "circle-group")
    const makeCircle = (d: number, hide: boolean) => {
        for (let i = 0; i < sameClassesData.length; i++) {
            const yScale = yScales[i];
            const className = sameClassesData[i].className;
            const data = sameClassesData[i].datas;
            const circleEl = circleGroups
                .append("circle")
                .datum({
                    data,
                    d
                })
                .attr("cx", xScale(d))
                .attr("cy", yScale(y(data[d])))
                .attr("r", "5")
                .attr("fill", "white")
                .attr("stroke", "#bcbfc3")
                .attr("stroke-width", "2")
                .attr("class", hide ? "hide" : "")
                .attr("dataType", className as unknown as string)
                .attr("index", d)
            circles.push(circleEl.node() as SVGElement)
        }
    }

    const selectedCircle = (d: number) => {

        const prevSelected = circleGroups
            .selectAll(`circle.show:not([index="${d}"])`)
            .nodes()
        const curSelected = circleGroups
            .selectAll(`circle[index="${d}"]:not(.show)`)
            .nodes()

        for (const node of prevSelected) {
            (node as SVGCircleElement).classList.remove("show")
        }
        for (const node of curSelected) {
            (node as SVGCircleElement).classList.add("show")
        }
    }


    //Draw path And Gradient

    for (let i = 0; i < sameClassesData.length; i++) {
        const datas = sameClassesData[i].datas;
        const dataClass = sameClassesData[i].className as unknown as string
        const hasGradient = areaGradient.findIndex((v) => v.dataClass === dataClass) >= 0
        const yScale = yScales[i];
        const curve = d3.curveLinear
        // Path
        const line = d3.line<T>()
            .defined(i => true)
            .curve(curve)
            .x((_, i) => xScale(i))
            .y((d) => yScale(y(d)));

        const path = lineGroups
            .attr("id", "test")
            .append("path")
            .datum(datas)
            .attr("fill", "none")
            .attr("stroke", "#bcbfc3")
            .attr("stroke-width", 2)
            .attr("stroke-linejoin", "round")
            .attr("stroke-linecap", "round")
            .attr("d", line)
            .attr("id", dataClass);

        path
            .attr("stroke-dasharray", path.node()?.getTotalLength() || 0)
            .attr("stroke-dashoffset", path.node()?.getTotalLength() || 0)

        if (hasGradient) {
            //Gradient Area
            const area = d3.area<T>()
                .defined((_, i) => true)
                .curve(curve)
                .x((_, i) => xScale(i))
                .y0(yScale(0))
                .y1((v, index) => yScale(y(v)));

            dashboardGroups
                .append("path")
                .attr("fill", `url(#${dataClass})`)
                .attr("d", area(sameClassesData[i].datas))
                .attr("id", `area-${dataClass}`)
        }
        const less = maximumCircle < maximumLengthOfDatas;
        const distance = maximumLengthOfDatas / maximumCircle;
        const distanceRange = new Array(maximumLengthOfDatas).fill("").map((_, i) => {
            return Math.floor(i * distance)
        })


        for (let i = 0; i < maximumLengthOfDatas; i++) {
            if (less) {
                const noHasIndex = distanceRange.findIndex((v) => i === v) < 0;
                makeCircle(i, noHasIndex)
            } else {
                makeCircle(i, false)
            }
        }
    }

    //Line generater
    let lines: SVGElement[] = [];
    const makeDashedLine = (event: Event, d: number) => {
        for (const line of lines) {
            line.remove()
        }
        lines = []
        const yValues = sameClassesData.map((v, i) => {
            const { className, datas } = v;
            const yScale = yScales[i]
            const value: number = datas[d] ? yScale(y(datas[d])) : 0;

            return {
                value,
                className
            }
        }).sort((a, b) => a.value - b.value)

        for (let i = 0; i < yValues.length; i++) {
            const y2 = yValues[i + 1]?.value
            const line = svg
                .insert("line", ":first-child")
                .attr("dataType", yValues[i].className as unknown as string)
                .attr("x1", xScale(d))
                .attr("x2", xScale(d))
                .attr("y1", yValues[i].value)
                .attr("y2", y2 || height - bottom)
                .attr("stroke", "#bcbfc3")
                .attr("stroke-width", "1")
                .attr("stroke-dasharray", "2")
            lines.push(line.node() as SVGElement)
            line.lower()
        }
    }

    //Box.
    const graphWidth = width - left - right
    const boxWidth = graphWidth / (maximumLengthOfDatas - 1)
    const boxIndexs = new Array(maximumLengthOfDatas).fill("").map((_, i) => i);
    const boxes = svg
        .append("g")
        .attr("t-id", "box-group")
        .on("mouseleave", mouseoutEvent)
        .selectAll("rect")
        .data(boxIndexs)
        .join("rect")
        .attr("width", boxWidth)
        .attr("height", height - bottom - top)
        .attr("y", top)
        .attr("x", (index) => {
            const originX = boxWidth * index + left;
            const move = boxWidth / 2;
            return originX - move
        })
        .attr("fill", "transparent")
        .on("mouseenter", (e, d) => {
            makeDashedLine(e, d)
            selectedCircle(d)
        })
        .on("mousemove", (e, d) => {
            const datas = sameClassesData.map((v, i) => {
                return v.datas[d]
            })
            mousemoveEvent(e, datas)
        })
    //XAxis
    const axisGroup = svg.append("g")
        .attr("class", "axisGroup")
    const minimumXAxisLabel = axisGroup
        .append("text")
        .attr("x", xScale(xDomain[0]))
        .attr("y", yRange[0])
        .attr("dy", "0.8em")
        .attr("alignment-baseline", "hanging")
        .attr("text-anchor", 'left')
        .text(xAxislabel("start"))
    const midium = Math.floor(maximumLengthOfDatas / 2)
    const midiumXaxisLabel = maximumLengthOfDatas > 2 ? axisGroup
        .append("text")
        .attr("x", xScale(midium))
        .attr("y", yRange[0])
        .attr("dy", "0.8em")
        .attr("alignment-baseline", "hanging")
        .attr("text-anchor", 'middle')
        .text(xAxislabel("midium")) : null

    const maximumXAxisLabel = axisGroup
        .append("text")
        .attr("x", xScale(xDomain[1]))
        .attr("y", yRange[0])
        .attr("dy", "0.8em")
        .attr("alignment-baseline", "hanging")
        .attr("text-anchor", 'end')
        .text(xAxislabel("end"));

    // resize Event 
    let timeout: any;
    const resizeEvent = () => {
        width = node.getBoundingClientRect().width
        height = node.getBoundingClientRect().height
        xRange = [right, width - left];
        yRange = [height - bottom, top];
        xScale = d3.scaleLinear(xDomain, xRange);
        yScales = yDomains.map((v) => {
            return d3.scaleLinear(v as number[], yRange);
        })
        if (timeout) {
            clearTimeout(timeout)
        }
        timeout = setTimeout(() => {
            //Svg 
            svg
                .attr("width", width)
                .attr("height", height)
                .attr("viewBox", [0, 0, width, height])
            //Path, Gradient
            for (let i = 0; i < sameClassesData.length; i++) {
                const datas = sameClassesData[i].datas;
                const dataClass = sameClassesData[i].className as unknown as string
                const hasGradient = areaGradient.findIndex((v) => v.dataClass === dataClass) >= 0
                const yScale = yScales[i];
                const curve = d3.curveLinear
                const line = d3.line<T>()
                    .defined(i => true)
                    .curve(curve)
                    .x((_, i) => xScale(i))
                    .y((d) => yScale(y(d)));

                lineGroups
                    .select(`path:nth-child(${i + 1})`)
                    .datum(datas)
                    .attr("d", (v) => line(v))
                    .attr("stroke-dasharray", "0")

                circleGroups
                    .selectAll(`circle[dataType=${dataClass}]`)
                    .attr("cx", (_d) => {
                        const { d } = _d as { data: T[], d: number }
                        return xScale(d)
                    })
                    .attr("cy", (_d) => {
                        const { data, d } = _d as { data: T[], d: number }
                        return yScale(y(data[d]))
                    })

                if (hasGradient) {
                    //Gradient Area
                    const area = d3.area<T>()
                        .defined((_, i) => true)
                        .curve(curve)
                        .x((_, i) => xScale(i))
                        .y0(yScale(0))
                        .y1((v, index) => yScale(y(v)));

                    dashboardGroups
                        .select("#area-" + dataClass)
                        .attr("d", area(sameClassesData[i].datas))
                }
            }
            // //Box
            const graphWidth = width - left - right
            const boxWidth = graphWidth / (maximumLengthOfDatas - 1)
            boxes
                .attr("width", boxWidth)
                .attr("x", (index) => {
                    const originX = boxWidth * index + left;
                    const move = boxWidth / 2;
                    return originX - move
                })
            //Xaxis
            minimumXAxisLabel
                .attr("x", xScale(xDomain[0]))
                .attr("y", yRange[0])
            midiumXaxisLabel
                ?.attr("x", xScale(midium))
                ?.attr("y", yRange[0])
            maximumXAxisLabel
                .attr("x", xScale(xDomain[1]))
                .attr("y", yRange[0])
        }, 100)
    }

    window.addEventListener("resize", resizeEvent)

    node.append(svg.node() as SVGSVGElement)
    return {
        node: svg.node(),
        resizeEvent
    }
}